"use client"

import React, { useCallback, useState, useEffect, useRef } from "react"
import { Plus, FilePenLine, Trash, Search, Images } from "lucide-react"
import Modal from "../../components/Modal"
import FotosSucursalModal from "../../components/FotosSucursalModal"
import { getSucursales, getSucursalesDev, createSucursal, updateSucursal } from "../../services/sucursalService"
import { getEmpresas } from "../../services/empresaService"
import "./Sucursal.css"
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet"
import "leaflet/dist/leaflet.css"
import L from "leaflet"

// Componente para actualizar la vista del mapa cuando cambian las coordenadas
const MapUpdater = ({ center, zoom }) => {
  const map = useMap()
  useEffect(() => {
    if (center) {
      map.setView(center, zoom || map.getZoom())
    }
  }, [center, zoom, map])
  return null
}

const LocationPicker = ({ setLatitud, setLongitud }) => {
  useMapEvents({
    click(e) {
      setLatitud(e.latlng.lat)
      setLongitud(e.latlng.lng)
    },
  })
  return null
}

// Icono personalizado para el mapa
const customIcon = L.divIcon({
  html: `<div style="display: flex; justify-content: center; align-items: center; width: 30px; height: 30px; background: blue; border-radius: 50%; box-shadow: 0px 0px 5px rgba(0,0,0,0.2);">
           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#920606" stroke="black" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
             <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"></path>
             <circle cx="12" cy="9" r="2"></circle>
           </svg>
        </div>`,
  className: "",
  iconSize: [30, 30],
  iconAnchor: [15, 30],
})

// Función para formatear la hora en formato AM/PM
const formatTimeToAMPM = (timeString) => {
  if (!timeString) return "No definida"
  const [hours, minutes] = timeString.split(":")
  const hour = Number.parseInt(hours, 10)
  const ampm = hour >= 12 ? "PM" : "AM"
  const hour12 = hour % 12 || 12
  return `${hour12}:${minutes} ${ampm}`
}

const Sucursal = () => {
  const [role, setRole] = useState(localStorage.getItem("role"))
  const [fotosSucursal, setFotosSucursal] = useState(null) // sucursal seleccionada para gestionar fotos
  const [searchAddress, setSearchAddress] = useState("")
  const [mapCenter, setMapCenter] = useState([-0.1807, -78.4678])
  const [mapZoom, setMapZoom] = useState(13)
  const [searchError, setSearchError] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [empresas, setEmpresas] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [editing, setEditing] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [sucursalToDelete, setSucursalToDelete] = useState(null)
  const [filter, setFilter] = useState("active")
  const [sucursales, setSucursales] = useState([])

  // --- NUEVO: Estados para el filtro de empresas ---
  const [selectedEmpresaId, setSelectedEmpresaId] = useState("") // "" significa "Todas"
  const [isEmpresaDropdownOpen, setIsEmpresaDropdownOpen] = useState(false)
  const [empresaSearchText, setEmpresaSearchText] = useState("")
  const empresaDropdownRef = useRef(null)
  // ---

  const [formData, setFormData] = useState({
    id: "",
    nombre: "",
    ubicacion: "",
    empresa: { id: "" },
    preciotarifa: "",
    plazas: "",
    latitud: "",
    longitud: "",
    estado: "0",
    horaApertura: "",
    horaCierre: "",
  })

  const fetchEmpresas = async () => {
    try {
      const data = await getEmpresas()
      setEmpresas(data)
    } catch (error) {
      console.error("Error al cargar las empresas:", error.message)
    }
  }

  useEffect(() => {
    const handleStorageChange = () => setRole(localStorage.getItem("role"))
    window.addEventListener("storage", handleStorageChange)
    return () => window.removeEventListener("storage", handleStorageChange)
  }, [])

  useEffect(() => {
    fetchEmpresas()
  }, [])

  // --- NUEVO: Hook para cerrar el dropdown de empresas si se hace clic afuera ---
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (empresaDropdownRef.current && !empresaDropdownRef.current.contains(event.target)) {
        setIsEmpresaDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (isModalOpen) {
      fetchEmpresas()
    }
  }, [isModalOpen])

  const fetchAllSucursales = useCallback(async () => {
    try {
      const data = role === "DEVELOPER" ? await getSucursalesDev() : await getSucursales()
      setSucursales(data)
    } catch (error) {
      console.error(error.message)
    }
  }, [role])

  useEffect(() => {
    fetchAllSucursales()
  }, [fetchAllSucursales])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === "empresa.id") {
      setFormData((prev) => ({ ...prev, empresa: { ...prev.empresa, id: value } }))
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const action = editing ? updateSucursal(formData.id, formData) : createSucursal(formData)
    try {
      await action
      fetchAllSucursales()
      closeModal()
    } catch (error) {
      console.error(error.message)
    }
  }

  const handleNew = () => {
    setFormData({
      id: "",
      nombre: "",
      ubicacion: "",
      empresa: { id: "" },
      preciotarifa: "",
      plazas: "",
      latitud: "",
      longitud: "",
      estado: "0",
      horaApertura: "",
      horaCierre: "",
    })
    setSearchAddress("")
    setSearchError("")
    setMapCenter([-0.1807, -78.4678])
    setMapZoom(13)
    setEditing(false)
    setIsModalOpen(true)
  }

  const handleEdit = (sucursal) => {
    setFormData({
      id: sucursal.id,
      nombre: sucursal.nombre,
      ubicacion: sucursal.ubicacion,
      empresa: { id: sucursal.empresa?.id || "" },
      preciotarifa: sucursal.preciotarifa,
      plazas: sucursal.plazas || "",
      latitud: sucursal.latitud,
      longitud: sucursal.longitud,
      estado: sucursal.estado?.toString() || "0",
      horaApertura: sucursal.horaApertura || "",
      horaCierre: sucursal.horaCierre || "",
    })
    setSearchAddress("")
    setSearchError("")
    if (sucursal.latitud && sucursal.longitud) {
      setMapCenter([Number.parseFloat(sucursal.latitud), Number.parseFloat(sucursal.longitud)])
      setMapZoom(15)
    } else {
      setMapCenter([-0.1807, -78.4678])
      setMapZoom(13)
    }
    setEditing(true)
    setIsModalOpen(true)
  }

  const openDeleteConfirmation = (sucursal) => setSucursalToDelete(sucursal)

  const confirmDelete = async () => {
    if (sucursalToDelete) {
      try {
        await updateSucursal(sucursalToDelete.id, { ...sucursalToDelete, estado: "1" })
        fetchAllSucursales()
      } catch (error) {
        console.error(error.message)
      }
      closeDeleteModal()
    }
  }

  const closeDeleteModal = () => setSucursalToDelete(null)
  const closeModal = () => setIsModalOpen(false)
  const handleFilterChange = (e) => setFilter(e.target.value)
  const handleSearchChange = (e) => setSearchTerm(e.target.value)

  // --- NUEVO: Manejadores para el filtro de empresas ---
  const handleEmpresaSelect = (empresaId) => {
    setSelectedEmpresaId(empresaId)
    setIsEmpresaDropdownOpen(false)
    setEmpresaSearchText("")
  }

  // --- MODIFICADO: Lógica de filtrado con el nuevo filtro de empresa ---
  const filteredSucursales = sucursales
    .filter((suc) => {
      // 1. Filtro por estado (activo/inactivo)
      if (filter === "active") return suc.estado === 0 || suc.estado === "0"
      if (filter === "inactive") return suc.estado === 1 || suc.estado === "1"
      return true
    })
    .filter((suc) => {
      // 2. Filtro por búsqueda de nombre general
      if (!searchTerm) return true
      return suc.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    })
    .filter((suc) => {
      // 3. Filtro por empresa seleccionada
      if (!selectedEmpresaId) return true
      return suc.empresa?.id.toString() === selectedEmpresaId
    })

  const handleAddressSearch = async (e) => {
    e.preventDefault()
    if (!searchAddress.trim()) {
      setSearchError("Por favor ingrese una dirección")
      return
    }
    setIsSearching(true)
    setSearchError("")
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchAddress)}`,
      )
      const data = await response.json()
      if (data && data.length > 0) {
        const { lat, lon } = data[0]
        setMapCenter([Number.parseFloat(lat), Number.parseFloat(lon)])
        setMapZoom(16)
        setFormData((prev) => ({ ...prev, latitud: lat, longitud: lon }))
        setSearchError("")
      } else {
        setSearchError("No se encontró la dirección. Intente con otra búsqueda.")
      }
    } catch (error) {
      console.error("Error al buscar dirección:", error)
      setSearchError("Error al buscar la dirección. Intente nuevamente.")
    } finally {
      setIsSearching(false)
    }
  }

  const filteredEmpresasForDropdown = empresas.filter(empresa =>
    empresa.nombre.toLowerCase().includes(empresaSearchText.toLowerCase())
  );

  return (
    <div className="sucursal-page-container">
      <div className="sucursal-header">
        <h1 className="sucursal-title">Gestión de Sucursales</h1>
        <div className="sucursal-header-decoration"></div>
      </div>

      <div className="sucursal-table-container">
        <div className="sucursal-table-header">
          {/* */}
          <div className="sucursal-table-controls">
            {/* Filtro de Estado */}
            <div className="sucursal-filter-container">
              <label>Estado:</label>
              <select onChange={handleFilterChange} value={filter}>
                <option value="active">Activas</option>
                <option value="inactive">Inactivas</option>
                <option value="all">Todas</option>
              </select>
            </div>

            {/* NUEVO: Filtro de Empresas con Búsqueda */}
            <div className={`sucursal-custom-dropdown ${isEmpresaDropdownOpen ? 'sucursal-dropdown-open' : ''}`} ref={empresaDropdownRef}>
              <button className="sucursal-custom-dropdown-btn" onClick={() => setIsEmpresaDropdownOpen(!isEmpresaDropdownOpen)}>
                <span className="sucursal-custom-dropdown-btn-text">
                  {selectedEmpresaId ? empresas.find(e => e.id.toString() === selectedEmpresaId)?.nombre : "Todas las empresas"}
                </span>
              </button>
              {isEmpresaDropdownOpen && (
                <div className="sucursal-custom-dropdown-content">
                  <div className="sucursal-custom-dropdown-search-container">
                    <Search size={16} className="sucursal-custom-dropdown-search-icon" />
                    <input
                      type="text"
                      placeholder="Buscar empresa..."
                      className="sucursal-custom-dropdown-search"
                      value={empresaSearchText}
                      onChange={(e) => setEmpresaSearchText(e.target.value)}
                      autoFocus
                    />
                  </div>
                  <ul className="sucursal-custom-dropdown-list">
                    <li
                      className={`sucursal-custom-dropdown-item ${!selectedEmpresaId ? 'sucursal-item-selected' : ''}`}
                      onClick={() => handleEmpresaSelect("")}
                    >
                      Todas las empresas
                    </li>
                    {filteredEmpresasForDropdown.length > 0 ? (
                      filteredEmpresasForDropdown.map(empresa => (
                        <li
                          key={empresa.id}
                          className={`sucursal-custom-dropdown-item ${selectedEmpresaId === empresa.id.toString() ? 'sucursal-item-selected' : ''}`}
                          onClick={() => handleEmpresaSelect(empresa.id.toString())}
                        >
                          {empresa.nombre}
                        </li>
                      ))
                    ) : (
                      <li className="sucursal-custom-dropdown-item-empty">No se encontraron empresas</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Barra de Búsqueda General */}
            <div className="sucursal-search-bar-container">
              <Search size={16} className="sucursal-search-bar-icon" />
              <input
                type="text"
                className="sucursal-search-bar-input"
                placeholder="Buscar por nombre de sucursal..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>
          <div>
            {role === "DEVELOPER" && (
              <button onClick={handleNew} className="sucursal-btn-primary">
                <Plus size={16} />
                Nueva Sucursal
              </button>
            )}
          </div>
        </div>

        <div className="sucursal-table-responsive">
          <table className="sucursal-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nombre</th>
                <th>Ubicación</th>
                <th>Empresa</th>
                <th>Precio Tarifa</th>
                <th>Plazas</th>
                <th>Hora Apertura</th>
                <th>Hora Cierre</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            {/* */}
            <tbody>
              {filteredSucursales.length > 0 ? (
                filteredSucursales.map((suc) => (
                  <tr key={suc.id}>
                    <td><span className="sucursal-id-badge">{suc.id}</span></td>
                    <td>{suc.nombre}</td>
                    <td>{suc.ubicacion}</td>
                    <td>{suc.empresa?.nombre || "Sin Empresa"}</td>
                    <td><span className="sucursal-precio-badge">${suc.preciotarifa}</span></td>
                    <td><span className="sucursal-plazas-badge">{suc.plazas || 0}</span></td>
                    <td><span className="sucursal-time-badge">{formatTimeToAMPM(suc.horaApertura)}</span></td>
                    <td><span className="sucursal-time-badge">{formatTimeToAMPM(suc.horaCierre)}</span></td>
                    <td>
                      <span className={`sucursal-estado-badge ${suc.estado === 0 || suc.estado === "0" ? "sucursal-estado-badge--activo" : "sucursal-estado-badge--inactivo"}`}>
                        {suc.estado === 0 || suc.estado === "0" ? "Activo" : "Inactivo"}
                      </span>
                    </td>
                    <td>
                      <div className="sucursal-action-buttons">
                        <button onClick={() => handleEdit(suc)} className="sucursal-action-button"><FilePenLine size={18} className="sucursal-icon--edit" /></button>
                        {role === "DEVELOPER" && (
                          <button onClick={() => setFotosSucursal(suc)} className="sucursal-action-button" title="Gestionar fotos"><Images size={18} className="sucursal-icon--fotos" /></button>
                        )}
                        {(suc.estado === 0 || suc.estado === "0") && (
                          <button onClick={() => openDeleteConfirmation(suc)} className="sucursal-action-button"><Trash size={18} className="sucursal-icon--delete" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">
                    <div className="sucursal-empty-message">
                      No se encontraron sucursales con los filtros aplicados
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal para Crear/Actualizar Sucursal */}
      <Modal isOpen={isModalOpen} onClose={closeModal}>
        <div className="sucursal-modal-header">
          <h2 className="sucursal-modal-title">{editing ? "Actualizar Sucursal" : "Crear Sucursal"}</h2>
        </div>
        <div className="sucursal-modal-content">
          <div className="sucursal-modal-two-columns">
            <div className="sucursal-modal-column sucursal-modal-form-column">
              <form onSubmit={handleSubmit} id="sucursal-form">
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Nombre:</label>
                  <input type="text" name="nombre" value={formData.nombre} onChange={handleChange} required className="sucursal-modal-input" />
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Ubicación:</label>
                  <input type="text" name="ubicacion" value={formData.ubicacion} onChange={handleChange} required className="sucursal-modal-input" />
                </div>
                {role !== "ADMIN" && (
                  <div className="sucursal-modal-form-group">
                    <label className="sucursal-modal-label">Empresa:</label>
                    <select name="empresa.id" value={formData.empresa.id} onChange={handleChange} required className="sucursal-modal-select">
                      <option value="">-- Seleccione una empresa --</option>
                      {empresas.map((empresa) => (<option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>))}
                    </select>
                  </div>
                )}
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Precio Tarifa:</label>
                  <input type="number" step="0.01" name="preciotarifa" value={formData.preciotarifa} onChange={handleChange} required className="sucursal-modal-input" />
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Plazas:</label>
                  <div onClick={() => { if (role === "ADMIN") { alert("Este campo no se puede modificar,si quiere actualizar contactese con su proveedor.") } }} className="sucursal-input-wrapper">
                    <input type="number" name="plazas" value={formData.plazas} onChange={handleChange} required className="sucursal-modal-input" min="1" readOnly={role === "ADMIN"} title={role === "ADMIN" ? "Este campo no se puede modificar" : ""} style={{ cursor: role === "ADMIN" ? "not-allowed" : "auto", backgroundColor: role === "ADMIN" ? "var(--background)" : "white", color: role === "ADMIN" ? "var(--text-secondary)" : "black", opacity: role === "ADMIN" ? 0.6 : 1 }} />
                  </div>
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Hora Apertura:</label>
                  <input type="time" name="horaApertura" value={formData.horaApertura} onChange={handleChange} className="sucursal-modal-input" />
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Hora Cierre:</label>
                  <input type="time" name="horaCierre" value={formData.horaCierre} onChange={handleChange} className="sucursal-modal-input" />
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Estado:</label>
                  <select name="estado" value={formData.estado} onChange={handleChange} required className="sucursal-modal-select">
                    <option value="0">Activo</option>
                    <option value="1">Inactivo</option>
                  </select>
                </div>
                <div className="sucursal-modal-form-group">
                  <label className="sucursal-modal-label">Coordenadas:</label>
                  <div className="sucursal-coordinates-group">
                    <div><input type="text" value={formData.latitud ? `Lat: ${formData.latitud}` : "Latitud: No seleccionada"} readOnly className="sucursal-modal-input sucursal-coordinate-input" /></div>
                    <div><input type="text" value={formData.longitud ? `Lng: ${formData.longitud}` : "Longitud: No seleccionada"} readOnly className="sucursal-modal-input sucursal-coordinate-input" /></div>
                  </div>
                </div>
              </form>
            </div>
            <div className="sucursal-modal-column sucursal-modal-map-column">
              <div className="sucursal-address-search-container">
                <label className="sucursal-modal-label">Buscar ubicación:</label>
                <form onSubmit={handleAddressSearch} className="sucursal-address-search-form">
                  <input type="text" value={searchAddress} onChange={(e) => setSearchAddress(e.target.value)} placeholder="Ingrese una dirección o lugar" className="sucursal-modal-input sucursal-search-input" />
                  <button type="submit" className="sucursal-search-button" disabled={isSearching}>{isSearching ? <span className="sucursal-search-loading"></span> : <Search size={18} />}</button>
                </form>
                {searchError && <p className="sucursal-search-error">{searchError}</p>}
              </div>
              <div className="sucursal-map-container">
                <MapContainer center={mapCenter} zoom={mapZoom} style={{ height: "100%", width: "100%" }}>
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  <LocationPicker setLatitud={(lat) => setFormData((prev) => ({ ...prev, latitud: lat }))} setLongitud={(lng) => setFormData((prev) => ({ ...prev, longitud: lng }))} />
                  {formData.latitud && formData.longitud && (<Marker position={[formData.latitud, formData.longitud]} icon={customIcon} />)}
                  <MapUpdater center={mapCenter} zoom={mapZoom} />
                </MapContainer>
              </div>
              <p className="sucursal-map-instructions">Haz clic en el mapa para seleccionar la ubicación o busca una dirección</p>
            </div>
          </div>
        </div>
        <div className="sucursal-modal-actions">
          <button type="submit" form="sucursal-form" className="sucursal-modal-btn sucursal-modal-btn--primary">{editing ? "Actualizar" : "Guardar"}</button>
          <button type="button" onClick={closeModal} className="sucursal-modal-btn sucursal-modal-btn--secondary">Cancelar</button>
        </div>
      </Modal>

      {/* Modal de confirmación para Eliminar */}
      <Modal isOpen={sucursalToDelete !== null} onClose={closeDeleteModal}>
        <div className="sucursal-modal-header"><h2 className="sucursal-modal-title">Confirmación</h2></div>
        <div className="sucursal-modal-content">
          <p>¿Está seguro que desea eliminar la sucursal <strong>{sucursalToDelete && sucursalToDelete.nombre}</strong>?</p>
          <div className="sucursal-modal-actions">
            <button onClick={confirmDelete} className="sucursal-modal-btn sucursal-modal-btn--primary">Confirmar</button>
            <button onClick={closeDeleteModal} className="sucursal-modal-btn sucursal-modal-btn--secondary">Cancelar</button>
          </div>
        </div>
      </Modal>

      {/* Modal de gestión de fotos (solo DEVELOPER) */}
      {fotosSucursal && (
        <FotosSucursalModal sucursal={fotosSucursal} onClose={() => setFotosSucursal(null)} />
      )}
    </div>
  )
}

export default Sucursal